import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect } from "react";
import LandingPage from "./pages/LandingPage";
import StreamPage from "./pages/StreamPage";
import TermsPage from "./pages/TermsPage";
import AccessPage from "./pages/AccessPage";
import SuccessPage from "./pages/SuccessPage";
import AdminPage from "./pages/AdminPage";
import ContactPage from "./pages/ContactPage";
import MaintenancePage from "./pages/MaintenancePage";
import StoriesPage from "./pages/StoriesPage";
import StoryDetailPage from "./pages/StoryDetailPage";
import SupportChat from "./components/SupportChat";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Function to remove Emergent badge - aggressive removal
const removeEmergentBadge = () => {
  // Try multiple selectors
  const selectors = [
    '[data-emergent-badge]',
    '.emergent-badge',
    'div[class*="emergent"]',
    'div[id*="emergent"]',
    'a[href*="emergentagent.com"]',
    'a[href*="emergent"]',
    'iframe[src*="emergent"]',
    '[class*="powered-by"]',
    '[class*="watermark"]'
  ];
  
  selectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      // Don't remove iframes that are videos
      if (el.tagName === 'IFRAME' && (el.src?.includes('vimeo') || el.classList.contains('trailer-iframe') || el.classList.contains('stream-iframe'))) {
        return;
      }
      el.remove(); // Actually remove from DOM
    });
  });
  
  // Also check for any element containing "Made with Emergent" or "Powered by" text
  document.querySelectorAll('div, a, span, p').forEach(el => {
    const text = el.innerText?.toLowerCase() || '';
    if (text.includes('made with emergent') || 
        text.includes('emergentagent') || 
        text.includes('powered by emergent') ||
        (text.includes('emergent') && el.querySelector('a'))) {
      el.remove(); // Actually remove from DOM
    }
  });
  
  // Check fixed position elements at bottom of screen (common badge location)
  document.querySelectorAll('div[style*="position: fixed"], div[style*="position:fixed"]').forEach(el => {
    const style = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    // If it's fixed at the bottom and small (likely a badge)
    if (rect.bottom > window.innerHeight - 100 && rect.height < 80) {
      const text = el.innerText?.toLowerCase() || '';
      if (text.includes('emergent') || text.includes('powered') || el.querySelector('a[href*="emergent"]')) {
        el.remove();
      }
    }
  });
};

function App() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${API_URL}/api/admin/maintenance`);
        const data = await response.json();
        setMaintenanceMode(data.enabled);
      } catch (error) {
        console.error('Failed to check maintenance status:', error);
      } finally {
        setCheckingMaintenance(false);
      }
    };

    checkMaintenance();
    
    // Check every 30 seconds
    const interval = setInterval(checkMaintenance, 30000);
    return () => clearInterval(interval);
  }, []);

  // Remove Emergent badge on mount and periodically
  useEffect(() => {
    // Run immediately
    removeEmergentBadge();
    
    // Run after a short delay (in case it's injected late)
    const timeout1 = setTimeout(removeEmergentBadge, 500);
    const timeout2 = setTimeout(removeEmergentBadge, 1000);
    const timeout3 = setTimeout(removeEmergentBadge, 2000);
    
    // Keep checking periodically
    const interval = setInterval(removeEmergentBadge, 3000);
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearInterval(interval);
    };
  }, []);

  // Show loading briefly while checking
  if (checkingMaintenance) {
    return <div className="App" style={{ background: '#0a0a0f', minHeight: '100vh' }}></div>;
  }

  return (
    <div className="App">
      <BrowserRouter>
        {maintenanceMode ? (
          <Routes>
            {/* Admin can still access during maintenance */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<MaintenancePage />} />
          </Routes>
        ) : (
          <>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/stream" element={<StreamPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/access" element={<AccessPage />} />
              <Route path="/success" element={<SuccessPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/stories" element={<StoriesPage />} />
              <Route path="/stories/:slug" element={<StoryDetailPage />} />
            </Routes>
            <SupportChat />
          </>
        )}
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
